import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { ImageUpload } from "@/components/ImageUpload";
import { RichTextEditor } from "@/components/RichTextEditor";
import { AYRSHIRE_TOWNS } from "@/hooks/use-town";
import { Pencil, Trash2, Plus, CheckCircle, XCircle, Star, Zap } from "lucide-react";
import { useCategories } from "@/hooks/use-categories";

const TOWNS = AYRSHIRE_TOWNS.filter(t => t !== "All Towns");

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface NewsItem {
  id: number;
  title: string;
  summary: string;
  content: string | null;
  category: string;
  town: string;
  imageUrl: string | null;
  sourceName: string | null;
  sourceLink: string | null;
  featured: boolean;
  breaking: boolean;
  approved: boolean;
  viewCount: number;
  createdAt: string;
}

const EMPTY_FORM = {
  title: "", summary: "", content: "", category: "Local News", town: "Ayr",
  imageUrl: "", sourceName: "", sourceLink: "", featured: false, breaking: false,
};

export default function AdminNewsManage() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<NewsItem | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const newsCategories = useCategories("news");

  if (authLoading) return null;
  if (user?.role !== "admin" && !(user as { isJournalist?: boolean } | null)?.isJournalist) return <Redirect to="/" />;

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const params = q ? `?search=${encodeURIComponent(q)}&limit=100` : "?limit=100";
      const res = await fetch(`/api/admin/news${params}`, { headers: authHeaders() });
      const data = await res.json();
      setItems(data.items ?? []);
    } catch {
      toast({ title: "Failed to load news", variant: "destructive", action: <ToastAction altText="Reload page" onClick={() => window.location.reload()}>Reload</ToastAction> });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }));

  const openEdit = (item: NewsItem) => {
    setEditing(item);
    setForm({
      title: item.title,
      summary: item.summary,
      content: item.content ?? "",
      category: item.category,
      town: item.town,
      imageUrl: item.imageUrl ?? "",
      sourceName: item.sourceName ?? "",
      sourceLink: item.sourceLink ?? "",
      featured: item.featured,
      breaking: item.breaking,
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    if (!form.title.trim() || !form.summary.trim()) {
      toast({ title: "Title and summary are required", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/news/${editing.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ ...form, content: form.content || form.summary }),
      });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Article updated" });
      setEditing(null);
      void load(search);
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this news article? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`/api/news/${id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Article deleted" });
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const toggleApproved = async (item: NewsItem) => {
    try {
      await fetch(`/api/news/${item.id}/approve`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ approved: !item.approved }),
      });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, approved: !i.approved } : i));
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    }
  };

  const filtered = items.filter(i =>
    !search || i.title.toLowerCase().includes(search.toLowerCase()) ||
    i.town.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout current="/admin/news">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary">Manage News</h1>
            <p className="text-sm text-slate-500">{items.length} articles total</p>
          </div>
          <button
            onClick={() => setLocation("/admin/news/create")}
            className="flex items-center gap-2 bg-primary text-white font-bold px-4 py-2.5 rounded-xl text-sm hover:bg-primary/90 transition-colors"
          >
            <Plus size={16} /> Post New Article
          </button>
        </div>

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by title or town…"
          className="w-full max-w-sm border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary/40"
        />

        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-slate-400 text-sm">No articles found.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map(item => (
              <div key={item.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex gap-4 items-start">
                {item.imageUrl && (
                  <img src={item.imageUrl} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 text-sm leading-tight">{item.title}</span>
                    {item.featured && <span className="flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full"><Star size={10} /> Featured</span>}
                    {item.breaking && <span className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full"><Zap size={10} /> Breaking</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-slate-400">{item.town}</span>
                    <span className="text-xs text-slate-300">·</span>
                    <span className="text-xs text-slate-400">{item.category}</span>
                    <span className="text-xs text-slate-300">·</span>
                    <span className="text-xs text-slate-400">{new Date(item.createdAt).toLocaleDateString("en-GB")}</span>
                    {item.viewCount > 0 && (
                      <>
                        <span className="text-xs text-slate-300">·</span>
                        <span className="text-xs font-medium text-indigo-600">👁 {item.viewCount.toLocaleString()}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">{item.summary}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => void toggleApproved(item)}
                    title={item.approved ? "Approved — click to unpublish" : "Pending — click to approve"}
                    className={`p-1.5 rounded-lg transition-colors ${item.approved ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}
                  >
                    {item.approved ? <CheckCircle size={18} /> : <XCircle size={18} />}
                  </button>
                  <button
                    onClick={() => openEdit(item)}
                    className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => void handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-primary">Edit Article</h2>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
            </div>
            <form onSubmit={e => void handleSave(e)} className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Title *</label>
                <input
                  required value={form.title} onChange={e => set("title", e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary/40"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Category</label>
                  <select value={form.category} onChange={e => set("category", e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none bg-white">
                    {newsCategories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Town</label>
                  <select value={form.town} onChange={e => set("town", e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none bg-white">
                    {TOWNS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Summary *</label>
                <textarea required value={form.summary} onChange={e => set("summary", e.target.value)} rows={2}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary/40 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Full Content</label>
                <RichTextEditor
                  value={form.content}
                  onChange={v => set("content", v)}
                  placeholder="Write the full article here — use H2 for section headings, blockquotes for notable quotes…"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Cover Image</label>
                <ImageUpload value={form.imageUrl} onChange={url => set("imageUrl", url)} enableCrop aspect={16 / 9} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Source Name</label>
                  <input value={form.sourceName} onChange={e => set("sourceName", e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/40" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Source Link</label>
                  <input type="url" value={form.sourceLink} onChange={e => set("sourceLink", e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/40" />
                </div>
              </div>
              <div className="flex gap-6">
                {[{ key: "featured", label: "Featured", color: "bg-primary" }, { key: "breaking", label: "Breaking News", color: "bg-red-500" }].map(({ key, label, color }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <div onClick={() => set(key, !(form as Record<string, boolean | string>)[key] as boolean)}
                      className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${(form as Record<string, boolean | string>)[key] ? color : "bg-slate-300"}`}>
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${(form as Record<string, boolean | string>)[key] ? "translate-x-5" : ""}`} />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving}
                  className="bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded-xl text-sm transition-colors">
                  {saving ? "Saving…" : "Save Changes"}
                </button>
                <button type="button" onClick={() => setEditing(null)}
                  className="px-6 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
